import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { buildMinioProxyUrl } from '../utils/authedResource';

// --- pdf.js worker 设置 ------------------------------------------------------
// 使用本地的 pdf.worker.min.js 文件，避免从 CDN 加载失败的问题
const LOCAL_WORKER = '/pdf.worker.min.js';

// 确保在组件加载时设置 workerSrc，但不在模块级别直接执行
// ----------------------------------------------------------------------------

const styles = {
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    background: 'var(--bg-primary)',
    border: '1px solid rgba(148,163,184,0.25)',
    borderRadius: '12px',
    overflow: 'hidden',
    minHeight: 420
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0.6rem 0.9rem',
    borderBottom: '1px solid rgba(148,163,184,0.25)',
    flexWrap: 'wrap',
    background: 'var(--bg-secondary)'
  },
  group: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    flexWrap: 'wrap'
  },
  btn: {
    border: '1px solid rgba(148,163,184,0.35)',
    background: 'var(--bg-primary)',
    color: 'var(--text-primary)',
    borderRadius: '8px',
    padding: '0.35rem 0.75rem',
    fontSize: '0.8rem',
    cursor: 'pointer'
  },
  btnDisabled: { opacity: 0.5, cursor: 'not-allowed' },
  info: { fontSize: '0.75rem', color: 'var(--text-secondary)' },
  canvasArea: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: 'rgba(15,23,42,0.04)',
    padding: '1rem'
  },
  error: { color: 'var(--danger-color)', fontSize: '0.9rem' }
};

export default function SimplePdfViewer({ url, fileName, workerSrc, viewMode = 'single' }) {
  const [numPages, setNumPages] = useState(null);
  const [page, setPage] = useState(1);
  const [scale, setScale] = useState(1);
  const [error, setError] = useState(null);
  const [viewportWidth, setViewportWidth] = useState(720);
  const canvasAreaRef = useRef(null);

  // 组件初始化时设置 workerSrc
  useEffect(() => {
    // 优先使用外部传入的 workerSrc，否则使用本地文件
    const finalWorkerSrc = workerSrc || LOCAL_WORKER;
    console.log('[PDF] 设置 workerSrc:', finalWorkerSrc);
    pdfjs.GlobalWorkerOptions.workerSrc = finalWorkerSrc;
  }, [workerSrc]);

  useEffect(() => {
    setNumPages(null);
    setPage(1);
    setScale(1);
    setError(null);
  }, [url]);

  useEffect(() => {
    const updateWidth = () => {
      const el = canvasAreaRef.current;
      if (!el) return;
      const w = Math.floor(el.clientWidth || 0);
      if (w > 0) setViewportWidth(w);
    };

    updateWidth();

    let observer = null;
    if (typeof ResizeObserver !== 'undefined' && canvasAreaRef.current) {
      observer = new ResizeObserver(() => updateWidth());
      observer.observe(canvasAreaRef.current);
    }

    window.addEventListener('resize', updateWidth);
    return () => {
      window.removeEventListener('resize', updateWidth);
      if (observer) observer.disconnect();
    };
  }, []);

  const token = (() => {
    try { return window.localStorage.getItem('token') || ''; } catch (e) { return ''; }
  })();

  // 若传入 presigned URL，优先走后端 /api/minio/object 代理（更稳，且可传 Authorization）
  const finalUrl = (() => {
    const s = String(url || '').trim();
    if (!s) return s;
    if (s.startsWith('blob:') || s.startsWith('data:')) return s;
    if (/X-Amz-Algorithm=AWS4-HMAC-SHA256/i.test(s) || /[?&]response-content-disposition=|[?&]response-content-type=/i.test(s)) {
      return buildMinioProxyUrl(s) || s;
    }
    return s;
  })();

  const shouldPassHeaders = (() => {
    const s = String(finalUrl || '').trim();
    if (!s) return false;
    if (s.startsWith('blob:') || s.startsWith('data:')) return false;
    return true;
  })();

  const fileProp = useMemo(() => {
    if (!shouldPassHeaders) return finalUrl;
    return {
      url: finalUrl,
      httpHeaders: token ? { Authorization: 'Bearer ' + token } : {},
      withCredentials: true
    };
  }, [shouldPassHeaders, finalUrl, token]);

  const pdfOptions = useMemo(() => ({
    disableRange: true,
    disableStream: true,
    disableAutoFetch: true
  }), []);

  const onLoadSuccess = ({ numPages: n }) => {
    const pages = n || 1;
    setNumPages(pages);
    setPage((prev) => {
      if (prev < 1) return 1;
      if (prev > pages) return pages;
      return prev;
    });
  };

  const onLoadError = (e) => {
    console.error('[PDF] load error:', e);
    // 若是 worker 相关错误，给出提示
    const msg = e?.message || '';
    if (/WorkerMessageHandler/.test(msg) || /Setting up fake worker/.test(msg)) {
      console.warn('[PDF] 可能的原因：workerSrc 未找到或版本不匹配。请确认 public/pdf.worker.min.js 是否存在且文件正常。');
    }
    setError(msg || 'PDF 加载失败');
  };

  const changePage = useCallback(delta => {
    setPage(p => {
      const next = p + delta;
      if (next < 1) return 1;
      if (numPages && next > numPages) return numPages;
      return next;
    });
  }, [numPages]);

  const zoom = useCallback(delta => {
    setScale(s => {
      let next = s + delta;
      if (next < 0.5) next = 0.5;
      if (next > 3) next = 3;
      return Math.round(next * 100) / 100;
    });
  }, []);

  const isAllPages = String(viewMode || '').toLowerCase() === 'all';
  const baseWidth = useMemo(() => {
    const safe = Math.max(320, Math.min(720, Math.floor((viewportWidth || 720) - 32)));
    return safe;
  }, [viewportWidth]);
  const renderWidth = useMemo(() => Math.max(240, Math.min(1600, baseWidth)), [baseWidth]);

  return (
    <div style={styles.wrapper}>
      <div style={styles.controls}>
        <div style={styles.group}>
          <button
            type="button"
            style={{ ...styles.btn, ...(scale <= 0.5 ? styles.btnDisabled : {}) }}
            disabled={scale <= 0.5}
            onClick={() => zoom(-0.2)}
          >缩小</button>
          <button type="button" style={styles.btn} onClick={() => setScale(1)}>重置</button>
            <button
              type="button"
              style={{ ...styles.btn, ...(scale >= 3 ? styles.btnDisabled : {}) }}
              disabled={scale >= 3}
              onClick={() => zoom(0.2)}
            >放大</button>
        </div>
        {!isAllPages && (
          <div style={styles.group}>
            <button
              type="button"
              style={{ ...styles.btn, ...(page <= 1 ? styles.btnDisabled : {}) }}
              disabled={page <= 1}
              onClick={() => changePage(-1)}
            >上一页</button>
            <span style={styles.info}>第 {page} / {numPages || '?' } 页</span>
            <button
              type="button"
              style={{ ...styles.btn, ...(!numPages || page >= numPages ? styles.btnDisabled : {}) }}
              disabled={!numPages || page >= numPages}
              onClick={() => changePage(1)}
            >下一页</button>
          </div>
        )}
        {isAllPages && (
          <div style={styles.group}>
            <span style={styles.info}>共 {numPages || '?' } 页（连续滚动）</span>
          </div>
        )}
        <div style={styles.info}>{fileName} · {Math.round(scale * 100)}%</div>
      </div>
      <div ref={canvasAreaRef} style={{ ...styles.canvasArea, alignItems: isAllPages ? 'flex-start' : styles.canvasArea.alignItems }}>
        {error && <div style={styles.error}>{error}</div>}
        {!error && (
          <Document
            key={finalUrl}
            file={fileProp}
            options={pdfOptions}
            loading={<div style={styles.info}>正在加载 PDF…</div>}
            onLoadSuccess={onLoadSuccess}
            onLoadError={onLoadError}
            onSourceError={onLoadError}
          >
            {!isAllPages ? (
              <Page
                key={`page_${page}`}
                pageNumber={page}
                width={renderWidth}
                scale={scale}
                renderAnnotationLayer={false}
                renderTextLayer={false}
              />
            ) : (
              <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center' }}>
                {Array.from(new Array(numPages || 0), (el, index) => (
                  <Page
                    key={`p_${index + 1}`}
                    pageNumber={index + 1}
                    width={renderWidth}
                    scale={scale}
                    renderAnnotationLayer={false}
                    renderTextLayer={false}
                  />
                ))}
              </div>
            )}
          </Document>
        )}
      </div>
    </div>
  );
}